import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  SparkDrawerPortal,
  SparkIcon,
  Spinner,
  TagFeedback,
  TooltipTrigger,
} from '@spark';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { isGenerativeTask } from '@/shared/domain/taskTypes';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { resolveModelDrawerStatusText } from './trainingsUtils';
import ModelDetailsContent from './ModelDetailsContent';
import '@/features/tasks/styles/task-drawer.css';
import { GlobalContext } from '@/app/providers/globalState';
import { undeployFirstTooltip } from '@/shared/utils/deploymentUx';
import {
  TASK_DRAWER_MAX_WIDTH_PX,
  TASK_NESTED_MODEL_DRAWER_ROOT_CLASS_NAME,
} from '@/features/tasks/components/Task/taskDrawerLayout';

export default function ModelDetailsDrawer({
  open,
  onClose,
  distillationEventId,
  version,
  onDownloadModel,
  onDownloadBenchmark,
  onDeploy,
  onUndeploy,
  title,
  clientId = DEFAULT_CLIENT_ID,
  role,
  taskType,
  /** Distillation event id of the benchmark currently shown on the main Evaluation page. */
  selectedBenchmarkEventId = null,
  /**
   * Called when the user picks a benchmark row from the Benchmarks tab.
   * The Evaluation page uses it to re-query metrics + samples for that exact run.
   */
  onSelectBenchmark,
  /** Drawer panel width; default matches standalone trainings usage. */
  drawerWidth = '85vw',
  /** Caps the drawer wrapper width (px); default matches standalone trainings usage. */
  drawerWrapperMaxWidth = 1100,
  /** When false, opening this drawer does not push/transform a parent drawer. */
  drawerPush = true,
}) {
  const { progressStatus } = useContext(GlobalContext);
  const nestedInTaskDrawer = drawerPush === false;
  // Nested stack uses CSS (task-drawer-panel-width) for exact width; avoid a
  // resize-driven px hook that widens the parent when the inner drawer opens.
  const resolvedDrawerWidth = nestedInTaskDrawer ? TASK_DRAWER_MAX_WIDTH_PX : drawerWidth;

  // Immediate click feedback for Deploy / Undeploy: the buttons otherwise only
  // spin once the usecase status flips to *_in_progress (websocket / refetch),
  // which lags the click by several seconds and looks stuck.
  const [deployRequestPending, setDeployRequestPending] = useState(false);
  const [undeployRequestPending, setUndeployRequestPending] = useState(false);

  // Training loaded by ModelDetailsContent — status fallback when the version
  // row itself has no `status` field.
  const [trainingFromDetails, setTrainingFromDetails] = useState(null);

  const statusText = resolveModelDrawerStatusText(version, trainingFromDetails);
  const statusLower = statusText.toLowerCase();
  const statusColor = (() => {
    const map = {
      running: 'processing',
      completed: 'success',
      failed: 'error',
      waiting: 'default',
      preparing: 'processing',
      paused: 'warning',
      training: 'processing',
      processing: 'processing',
      'in progress': 'processing',
    };
    return map[statusLower] || 'default';
  })();
  const showStatus = Boolean(statusText);
  // A deploy/undeploy lifecycle status (e.g. "Undeploy in Progress") replaces
  // the training status on the version row. The training itself is complete in
  // that state, so header actions must not disappear while it is active.
  const isDeployLifecycleStatus =
    statusLower === deploy_in_progress.toLowerCase() ||
    statusLower === undeploy_in_progress.toLowerCase();
  const isTrainingComplete = statusLower === 'completed' || isDeployLifecycleStatus;
  const canDownload = role !== 'Viewer' && isTrainingComplete;

  const downloadProgress = useMemo(() => {
    const usecaseId = version?.llm_usecase_id;
    const v = version?.version;
    if (!usecaseId || v === undefined || v === null) {
      return { inProgress: false, percent: null, message: '' };
    }

    const directKey = `download_${usecaseId}_${v}`;
    const item = progressStatus?.[directKey];
    if (item?.isDownload) {
      const inProgress = !item.isCompleted && !item.isError;
      const percent = Number.isFinite(Number(item.progress)) ? Math.round(Number(item.progress)) : null;
      const message = String(item.message || '');
      return { inProgress, percent, message };
    }

    // Fallback: find matching progress item by fields (in case keys differ).
    const entries = Object.values(progressStatus || {});
    const found = entries.find(
      (x) =>
        x?.isDownload &&
        String(x?.usecaseId || '') === String(usecaseId) &&
        Number(x?.version) === Number(v) &&
        !x?.isCompleted &&
        !x?.isError
    );
    if (!found) return { inProgress: false, percent: null, message: '' };
    return {
      inProgress: true,
      percent: Number.isFinite(Number(found.progress)) ? Math.round(Number(found.progress)) : null,
      message: String(found.message || ''),
    };
  }, [progressStatus, version?.llm_usecase_id, version?.version]);

  const isThisVersionDeployed = Boolean(
    version?.isThisVersionDeployed ??
    (version?.is_deployed && Number(version?.deployed_version) === Number(version?.version))
  );
  const hasAnyDeployedVersion = Boolean(
    version?.hasAnyDeployedVersion ??
    (version?.is_deployed && version?.deployed_version != null)
  );
  const deployedVersionNumber =
    version?.deployed_version != null ? Number(version.deployed_version) : null;

  // Only one version can be deployed per task. When viewing a non-deployed
  // version while another one is deployed, the Undeploy button targets that
  // deployed version so the user can free the slot before deploying this one.
  const undeployTargetVersion = isThisVersionDeployed
    ? Number(version?.version)
    : hasAnyDeployedVersion
      ? deployedVersionNumber
      : null;
  const undeployTargetsOtherVersion = !isThisVersionDeployed && hasAnyDeployedVersion;

  const usecaseStatus = String(version?.usecase_status || '');
  const isDeployInProgress =
    usecaseStatus === deploy_in_progress &&
    version?.deploying_version !== undefined &&
    Number(version?.deploying_version) === Number(version?.version);
  const isUndeployInProgress =
    usecaseStatus === undeploy_in_progress &&
    version?.undeploying_version !== undefined &&
    undeployTargetVersion != null &&
    Number(version?.undeploying_version) === Number(undeployTargetVersion);

  const canDeploy =
    role !== 'Viewer' && isTrainingComplete && !hasAnyDeployedVersion && !isThisVersionDeployed && !isGenerativeTask(taskType);
  const canUndeploy =
    role !== 'Viewer' &&
    isTrainingComplete &&
    (isThisVersionDeployed || undeployTargetsOtherVersion) &&
    !isGenerativeTask(taskType);

  const handleDeployClick = async () => {
    if (deployRequestPending) return;
    setDeployRequestPending(true);
    try {
      await onDeploy(version);
    } finally {
      setDeployRequestPending(false);
    }
  };

  const handleUndeployClick = async () => {
    if (undeployRequestPending) return;
    setUndeployRequestPending(true);
    try {
      await onUndeploy(version);
    } finally {
      setUndeployRequestPending(false);
    }
  };

  const downloadTooltipText = downloadProgress.inProgress
    ? (downloadProgress.message || 'Downloading…')
    : '';
  const undeployTooltipText = undeployTargetsOtherVersion
    ? undeployFirstTooltip(`V${deployedVersionNumber}.0`)
    : '';

  return (
    <SparkDrawerPortal
      open={open}
      onClose={onClose}
      belowNavbar={nestedInTaskDrawer}
      mask
      destroyOnClose
      className={
        nestedInTaskDrawer ? TASK_NESTED_MODEL_DRAWER_ROOT_CLASS_NAME : undefined
      }
    >
      <div
        style={{
          width: nestedInTaskDrawer ? 'var(--task-drawer-width, 917px)' : resolvedDrawerWidth,
          ...(nestedInTaskDrawer ? {} : { maxWidth: drawerWrapperMaxWidth }),
          height: '100%',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        {/* Header (formerly the antd Drawer title + extra). */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            borderBottom: '1px solid #E5E7EB',
            minHeight: 56,
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <Button
            variant="ghost"
            size="small"
            aria-label="Close"
            onClick={onClose}
            style={{ flexShrink: 0 }}
          >
            <SparkIcon slug="close" size="small" />
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, lineHeight: 1, flex: 1 }}>
            <div style={{ minWidth: 0, fontWeight: 600, fontSize: 14, lineHeight: 1 }}>
              <span style={{ display: 'inline-block', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                {title || 'Model details'}
              </span>
            </div>
            {showStatus ? (
              <span style={{ textTransform: 'capitalize', display: 'inline-flex', alignItems: 'center' }}>
                <TagFeedback
                  variant={statusColor}
                  label={statusLower === 'completed' ? 'Complete' : statusText}
                />
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {canDownload && onDownloadModel ? (
              <TooltipTrigger text={downloadTooltipText} disabled={!downloadTooltipText}>
                <Button
                  variant="secondary"
                  startIcon={
                    downloadProgress.inProgress
                      ? <Spinner size="small" />
                      : <SparkIcon slug="download" size="small" />
                  }
                  disabled={downloadProgress.inProgress}
                  onClick={() => onDownloadModel(version)}
                >
                  {downloadProgress.inProgress && downloadProgress.percent !== null
                    ? `Download (${downloadProgress.percent}%)`
                    : downloadProgress.inProgress
                      ? 'Download (in progress)'
                      : 'Download'}
                </Button>
              </TooltipTrigger>
            ) : null}
            {canUndeploy && onUndeploy ? (
              <TooltipTrigger text={undeployTooltipText} disabled={!undeployTooltipText}>
                <Button
                  variant="secondary"
                  startIcon={
                    (isUndeployInProgress || undeployRequestPending)
                      ? <Spinner size="small" />
                      : <SparkIcon slug="circle-minus" size="small" />
                  }
                  disabled={
                    isDeployInProgress ||
                    deployRequestPending ||
                    isUndeployInProgress ||
                    undeployRequestPending
                  }
                  disableWhenReadOnly
                  onClick={handleUndeployClick}
                >
                  {undeployTargetsOtherVersion
                    ? `Undeploy v${deployedVersionNumber}.0`
                    : 'Undeploy'}
                </Button>
              </TooltipTrigger>
            ) : null}
            {/* Hidden whenever any version of this task is deployed (this one or
                another): the Undeploy button owns that state, never both. */}
            {!hasAnyDeployedVersion && !isThisVersionDeployed && onDeploy ? (
              <Button
                variant="primary"
                startIcon={
                  (isDeployInProgress || deployRequestPending)
                    ? <Spinner size="small" />
                    : <SparkIcon slug="rocket" size="small" />
                }
                disabled={
                  !canDeploy ||
                  isUndeployInProgress ||
                  undeployRequestPending ||
                  isDeployInProgress ||
                  deployRequestPending
                }
                disableWhenReadOnly
                onClick={canDeploy ? handleDeployClick : undefined}
              >
                Deploy
              </Button>
            ) : null}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 24, position: 'relative' }}>
          {open ? (
            <ModelDetailsContent
              distillationEventId={distillationEventId}
              version={version}
              clientId={clientId}
              role={role}
              taskType={taskType}
              selectedBenchmarkEventId={selectedBenchmarkEventId}
              onSelectBenchmark={onSelectBenchmark}
              onDownloadBenchmark={onDownloadBenchmark}
              onDetailsLoaded={(resp) => setTrainingFromDetails(resp?.training || null)}
            />
          ) : null}
        </div>
      </div>
    </SparkDrawerPortal>
  );
}
